/* global Sys, Services, Resources */
Sys.ns("Services");

/**
 * The dialog configuration.
 *
 * @typedef {Object} DialogConfig
 * @property {string} [id] - The dialog id.
 * @property {string} [title] - The dialog title.
 * @property {Object} [fatal=false] - Flag indicating if this dialog represents a fatal condition.
 * @property {string} [text] - The dialog text.
 * @property {Object[]} [buttons] - The buttons.
 */

/**
 * The Dialog Service.
 *
 * @class Services.Dialog
 * @extends Services.Base
 */
Services.Dialog = {
    constructor: function() {
        Services.Dialog.superclass.constructor.apply(this, arguments);

        this._queue = [];
        this._currentDialog = null;
    },

    _setupEvents: function() {
        this.observable.on({
            "request:dialogWindow.showDialog": this.show.bind(this),
            "request:dialogWindow.removeDialog": this.remove.bind(this)
        });
    },

    /**
     * Checks if a dialog is being displayed.
     *
     * @returns {boolean} Whether or not a dialog is being displayed.
     */
    isShowing: function() {
        return this._currentDialog !== null;
    },

    /**
     * Checks if any dialogs queued.
     *
     * @returns {boolean} Whether or not a dialog queue empty.
     */
    isDialogsQueueEmpty: function() {
        return this._queue.length === 0;
    },

    /**
     * Shows the provided dialog.
     *
     * @param {DialogConfig} config The dialog config.
     * @returns {void}
     */
    show: function(config) {
        var updatedConfig = Sys.apply({}, config);

        if (Sys.isGcmEnabled) {
            this._gcmEnabled(config);
        }
        else {
            if (!updatedConfig.readyToShow) {
                if (!updatedConfig.id) {
                    updatedConfig.id = Sys.utils.pseudoGUID();
                }
                updatedConfig.text = this._parseTextSegments(config.text);
                updatedConfig.buttons = this._wrapButtonActions(config.buttons || [], updatedConfig.id);
                updatedConfig.readyToShow = true;
            }

            // Show immediately if nothing is showing
            if (!this._currentDialog) {
                this._currentDialog = updatedConfig;

                if (Resources.readData("isOSSIntegration")) {
                    this.publish("showOSSDialog", updatedConfig);
                }
                else {
                    this.publish("show", updatedConfig);
                }
            }
            // Show immediately if fatal and not currently showing a fatal
            else if (config.fatal && !this._currentDialog.fatal) {
                this.remove(this._currentDialog);

                this._currentDialog = updatedConfig;

                if (Resources.readData("isOSSIntegration")) {
                    this.publish("showOSSDialog", updatedConfig);
                }
                else {
                    this.publish("show", updatedConfig);
                }
            }
            // Queue it for later
            else {
                this._queue.push(updatedConfig);
            }
        }
    },

    /**
     * Gets the next dialog in queue.
     *
     * @returns {DialogConfig?} The config of the next dialog or null if it does not exist.
     */
    next: function() {
        this._currentDialog = null;

        return this._queue.shift() || null;
    },

    /**
     * Removes the dialog from view if currently shown or queue if not.
     *
     * @param {*} id The unique dialog identifier.
     * @returns {boolean} Whether or not the dialog was removed.
     */
    remove: function(id) {
        var queueLength;

        if (!Sys.isDefined(id)) {
            return false;
        }

        if (this._currentDialog && this._currentDialog.id === id) {
            // if requested dialog is active then hide it.
            this.publish("hide", id);

            this._currentDialog = null;

            if (this._queue.length > 0) {
                this.show(this.next());
            }

            return true;
        }

        queueLength = this._queue.length;

        this._queue = this._queue.filter(function(dialog) {
            return dialog.id !== id;
        });

        return queueLength !== this._queue.length;
    },

    /**
     * Removed currently opened dialog.
     *
     * @returns {void}
     */
    removeCurrent: function() {
        this.remove(this._currentDialog.id);
    },

    /**
     * Wraps the button actions so that the next dialog is shown when an option is selected.
     *
     * @private
     * @param {Object[]} buttons The dialog button definitions.
     * @param {*} id The unique dialog identifier.
     * @returns {Object[]} The buttons with wrapped actions.
     */
    _wrapButtonActions: function(buttons, id) {
        var me = this;

        return buttons.map(function(button) {
            return {
                primary: button.primary ? button.primary : false,
                text: button.text,
                logData: button.logData || {},
                action: function() {
                    var next;

                    if (button.action) {
                        button.action.call(button.scope);
                    }

                    me.publish("hide", id);

                    next = me.next();

                    if (next) {
                        me.show(next);
                    }
                    else {
                        me.observable.fireEvent("notify:customMessages.messagesClosed");
                        me.observable.fireEvent("notify:customMessages.allMessagesClosed");
                    }
                },
                buttonId: button.buttonId
            };
        });
    },

    /**
     * Modifies the config object for gcm.
     *
     * @private
     * @param {Object} config Object.
     * @returns {void}
     */
    _gcmEnabled: function(config) {
        var me = this,
            critical = config.severity === "stopped",
            gcmErrorConfig = {
                category: "MULTI_CHOICE_DIALOG",
                severity: critical ? "ERROR" : "INFO",
                message: Sys.isDefined(config.text) ? config.text : config.title,
                errorCode: "ERROR",
                actions: [],
                revert: false,
                extraParameters: {}
            },
            options = [];

        Sys.each(config.buttons, function(button) {
            options.push(button.text);
            gcmErrorConfig.actions.push(button.action);
        });

        gcmErrorConfig.extraParameters.options = options;

        me.observable.fireEvent("request:gcmProxy.handleError", gcmErrorConfig);
    },

    _parseTextSegments: function(text) {
        // Match markdown link: [link text](http://link.com)
        var matcher = /\[([^\]]+)\]\(([^)]+)\)/, segments, matching, title, url;

        if (matcher.test(text)) {
            segments = text.split(/(\[[^\]]+\]\([^)]+\))/);

            return segments.reduce(function(acc, segment) {
                if (matcher.test(segment)) {
                    matching = segment.match(matcher);
                    title = matching[1];
                    url = matching[2];

                    acc.push({ text: title, url: url });
                }
                else {
                    acc.push({ text: segment });
                }

                return acc;
            }, []);
        }

        return [
            {
                text: text || ""
            }
        ];
    }
};

Services.Dialog = Sys.extend(Services.Base, Services.Dialog, "Services.Dialog");
